/**
 * Self-schedule calendar — multi-slot picker using FullCalendar
 * directly. Shares the toolbar + week grid + busy-event rendering
 * with ManualCalendar; layers the self-schedule-unique UX on top:
 *
 *   - Per-day column checkbox  (toggles duration-sized slot
 *                              auto-generation for that day)
 *   - ALL master checkbox      (select / deselect all available days)
 *   - Slot pill with × button  (remove a single generated or custom
 *                              slot)
 *   - Drag-to-create custom    (user drags a window → custom slot)
 *
 * The state + slot generation live in `./selfSchedule/`. This file
 * is the FullCalendar glue: wire events, render UI bits, forward
 * callbacks.
 */
import { useCallback, useMemo, useRef } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import { KText } from '@/ui-library/components/text';
import { CalendarToolbar } from './CalendarToolbar';
import { BusyEventContent } from './events';
import type { ManualCalendarExternalEvent } from './hooks';
import {
  BG_EVENT_CLASSES,
  EVENT_PRIORITY,
  EVENT_STYLES,
  getScrollTimeForNow,
} from './events';
import {
  AllDaysCheckbox,
  DayCheckboxHeader,
  SlotPill,
} from './selfSchedule/ui';
import { useSelfScheduleSlots } from './selfSchedule';
import type { BusyEvent, Interval } from '../availability';
import './calendar.css';

interface SelfScheduleCalendarProps {
  duration: number;
  timezone?: string;
  onSlotsChange?: (_slots: Array<{ start: string; end: string }>) => void;
  /** Edit-mode seed. */
  initialSlots?: Array<{
    date: string;
    timeRanges: Array<{ startTime: string; endTime: string }>;
    enabled: boolean;
  }>;
  goToDate?: Date | string;
  /** Read-only busy events (interviewer + meeting-room). */
  externalEvents?: ManualCalendarExternalEvent[];
  /** Background overlays (unavailable hours, minimum notice). */
  overlayEvents?: EventInput[];
  onTimezoneChange?: (_timezone: string) => void;
  onActiveRangeChange?: (_range: { start: string; end: string }) => void;
  /** Common availability across selected interviewers. */
  availability?: Interval[];
  /** Busy events reshaped for the slot generator. */
  busyEventsForAlgorithm?: BusyEvent[];
  /** Whether any interviewer is picked — drives empty-state tooltips. */
  hasInterviewer?: boolean;
  minimumNoticeHours?: number;
}

const DISABLED_NO_INTERVIEWER = 'Pick an interviewer to select slots';
const DISABLED_NO_AVAILABILITY = 'Interviewer is not available on this day';
const DISABLED_PAST = 'Cannot select slots in the past';

export const SelfScheduleCalendar = ({
  duration,
  timezone: timezoneProp,
  onSlotsChange,
  initialSlots,
  goToDate,
  externalEvents = [],
  overlayEvents = [],
  onTimezoneChange,
  onActiveRangeChange,
  availability = [],
  busyEventsForAlgorithm = [],
  hasInterviewer = false,
  minimumNoticeHours = 0,
}: SelfScheduleCalendarProps) => {
  const calendarRef = useRef<FullCalendar>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const timezone = timezoneProp || moment.tz.guess();
  const onDatesSetRef = useRef<(() => void) | null>(null);

  const {
    activeSlots,
    daysMeta,
    allChecked,
    allIndeterminate,
    toggleDay,
    toggleAll,
    removeSlot,
    addCustomSlot,
  } = useSelfScheduleSlots({
    availability,
    busyEvents: busyEventsForAlgorithm,
    duration,
    timezone,
    minimumNoticeHours,
    hasInterviewer,
    initialSlots,
    onSlotsChange,
  });

  const handleTimezoneChange = useCallback(
    (tz: string) => onTimezoneChange?.(tz),
    [onTimezoneChange]
  );

  // ─── Event feeds ──────────────────────────────────────────────
  const busyEvents = useMemo<EventInput[]>(
    () =>
      externalEvents.map((evt) => ({
        id: evt.id ?? `ext-${evt.start}-${evt.end}`,
        start: evt.start,
        end: evt.end,
        title: evt.title ?? 'Busy',
        backgroundColor:
          evt.backgroundColor ?? EVENT_STYLES.busy.backgroundColor,
        borderColor: evt.borderColor ?? EVENT_STYLES.busy.borderColor,
        textColor: evt.textColor ?? EVENT_STYLES.busy.textColor,
        classNames: evt.classNames ?? 'im-interviewer-busy',
        editable: false,
        durationEditable: false,
        startEditable: false,
        extendedProps: {
          category: 'busy',
          priority: EVENT_PRIORITY.busy,
          tooltipTitle: evt.tooltipTitle ?? evt.title,
        },
      })),
    [externalEvents]
  );

  const pickedEvents = useMemo<EventInput[]>(
    () =>
      activeSlots.map((s) => ({
        id: s.id,
        start: s.start,
        end: s.end,
        title: 'Slot',
        backgroundColor: 'var(--kula-colors-primary-50)',
        borderColor: 'var(--kula-colors-primary-300)',
        textColor: 'var(--kula-colors-primary-700)',
        editable: true,
        durationEditable: s.isCustom,
        startEditable: true,
        classNames: 'im-self-slot',
        extendedProps: {
          category: 'picked',
          priority: EVENT_PRIORITY.available,
          isCustom: s.isCustom,
        },
      })),
    [activeSlots]
  );

  const pastOverlay = useMemo<EventInput>(
    () => ({
      start: moment()
        .tz(timezone)
        .subtract(10, 'year')
        .format('YYYY-MM-DDTHH:mm:ssZ'),
      end: moment().tz(timezone).format('YYYY-MM-DDTHH:mm:ssZ'),
      display: 'background',
      classNames: BG_EVENT_CLASSES.disabledSlots,
      color: 'transparent',
    }),
    [timezone]
  );

  const events = useMemo(
    () => [...overlayEvents, ...busyEvents, ...pickedEvents, pastOverlay],
    [overlayEvents, busyEvents, pickedEvents, pastOverlay]
  );

  // ─── Drag-to-create custom slot ──────────────────────────────
  // Any drag-select is snapped to exactly one `duration`-wide window
  // starting at the drag's start. The drag is only an anchor — the
  // user can't produce a slot longer than the interview duration,
  // which keeps payload slots matching the form's `duration` field.
  const handleSelect = useCallback(
    (info: { startStr: string }) => {
      const dateKey = moment.tz(info.startStr, timezone).format('YYYY-MM-DD');
      const startMs = moment.tz(info.startStr, timezone).valueOf();
      const endIso = moment
        .tz(startMs + duration * 60_000, timezone)
        .toISOString();
      addCustomSlot({
        id: `custom-${dateKey}-${Date.now()}`,
        start: info.startStr,
        end: endIso,
        dateKey,
        isCustom: true,
      });
      calendarRef.current?.getApi()?.unselect();
    },
    [addCustomSlot, duration, timezone]
  );

  // Single-click also creates a duration-sized slot so the user
  // doesn't have to drag to add one. Click lands on the 15-min grid,
  // and past cells are ignored — FullCalendar's `dateClick` fires
  // regardless of `selectAllow`, so the guard has to live here too.
  const handleDateClick = useCallback(
    (arg: { dateStr: string }) => {
      const dateKey = moment.tz(arg.dateStr, timezone).format('YYYY-MM-DD');
      const startMs = moment.tz(arg.dateStr, timezone).valueOf();
      const nowMs = moment().tz(timezone).valueOf();
      if (startMs < nowMs) return;
      const startIso = moment.tz(startMs, timezone).toISOString();
      const endIso = moment
        .tz(startMs + duration * 60_000, timezone)
        .toISOString();
      addCustomSlot({
        id: `custom-${dateKey}-${Date.now()}`,
        start: startIso,
        end: endIso,
        dateKey,
        isCustom: true,
      });
    },
    [addCustomSlot, duration, timezone]
  );

  // ─── Drag to move / resize a picked slot ─────────────────────
  const handleEventChange = useCallback(
    (arg: {
      event: {
        id: string;
        startStr: string;
        endStr: string;
        extendedProps: Record<string, unknown>;
      };
      revert: () => void;
    }) => {
      if (arg.event.extendedProps?.category !== 'picked') {
        arg.revert();
        return;
      }
      // Treat a moved/resized picked slot as a new custom slot + drop
      // the original. Simpler than tracking mutations in state and
      // mirrors how the legacy scheduler handled it.
      const dateKey = moment
        .tz(arg.event.startStr, timezone)
        .format('YYYY-MM-DD');
      removeSlot(arg.event.id);
      addCustomSlot({
        id: `custom-${dateKey}-${Date.now()}`,
        start: arg.event.startStr,
        end: arg.event.endStr,
        dateKey,
        isCustom: true,
      });
    },
    [addCustomSlot, removeSlot, timezone]
  );

  // ─── Initial focus date ──────────────────────────────────────
  const initialDate = useMemo(() => {
    if (goToDate) {
      return typeof goToDate === 'string' ? new Date(goToDate) : goToDate;
    }
    const first = activeSlots[0]?.start;
    return first ? new Date(first) : undefined;
  }, [goToDate, activeSlots]);

  // ─── Event rendering ─────────────────────────────────────────
  const renderEventContent = useCallback(
    (eventInfo: {
      event: {
        id: string;
        title: string;
        startStr: string;
        endStr: string;
        extendedProps: Record<string, unknown>;
      };
    }) => {
      const category = eventInfo.event.extendedProps?.category as string;
      if (category === 'busy') {
        return (
          <BusyEventContent
            title={eventInfo.event.title}
            tooltipTitle={
              eventInfo.event.extendedProps?.tooltipTitle as string | undefined
            }
            start={eventInfo.event.startStr}
            end={eventInfo.event.endStr}
            timezone={timezone}
          />
        );
      }
      if (category === 'picked') {
        return (
          <SlotPill
            slotId={eventInfo.event.id}
            startIso={eventInfo.event.startStr}
            endIso={eventInfo.event.endStr}
            timezone={timezone}
            onRemove={removeSlot}
          />
        );
      }
      return (
        <Box p="2px 4px" fontSize="11px" overflow="hidden">
          {eventInfo.event.title}
        </Box>
      );
    },
    [timezone, removeSlot]
  );

  // Drag-select can't extend past the form's `duration` — a drag of
  // any length still commits a single duration-wide slot, and this
  // guard caps the live drag visual so it never hints at a longer
  // window. Past time is also blocked.
  const selectAllow = useCallback(
    (info: { start: Date; startStr: string; endStr: string }) => {
      const now = moment().tz(timezone);
      const dur = moment
        .tz(info.endStr, timezone)
        .diff(moment.tz(info.startStr, timezone), 'minutes');
      return info.start >= now.toDate() && dur <= duration;
    },
    [duration, timezone]
  );

  const eventAllow = useCallback(
    (info: { start: Date }) => info.start >= moment().tz(timezone).toDate(),
    [timezone]
  );

  // ─── Day-header checkbox ─────────────────────────────────────
  const renderDayHeader = useCallback(
    (args: { date: Date }) => {
      const dateKey = moment(args.date).format('YYYY-MM-DD');
      const meta = daysMeta[dateKey];
      const isPast = moment(args.date).isBefore(moment(), 'day');

      // Priority order: no-interviewer > past > no-availability.
      let disabled = false;
      let reason: string | undefined;
      if (!hasInterviewer) {
        disabled = true;
        reason = DISABLED_NO_INTERVIEWER;
      } else if (isPast) {
        disabled = true;
        reason = DISABLED_PAST;
      } else if (!meta?.hasAvailability) {
        disabled = true;
        reason = DISABLED_NO_AVAILABILITY;
      }

      return (
        <DayCheckboxHeader
          date={args.date}
          isChecked={meta?.isSelected ?? false}
          isDisabled={disabled}
          disabledReason={reason}
          onToggle={(checked) => toggleDay(dateKey, checked)}
        />
      );
    },
    [daysMeta, hasInterviewer, toggleDay]
  );

  // ─── Master "ALL" disabled reason ────────────────────────────
  const allDisabled = !hasInterviewer;
  const allAvailableCount = Object.values(daysMeta).filter(
    (m) => m.hasAvailability
  ).length;
  const allReason = !hasInterviewer
    ? DISABLED_NO_INTERVIEWER
    : allAvailableCount === 0
      ? DISABLED_NO_AVAILABILITY
      : undefined;

  return (
    <Flex flexDir="column" h="100%" w="100%" className="im-calendar">
      <CalendarToolbar
        calendarRef={calendarRef}
        timezone={timezone}
        onTimezoneChange={handleTimezoneChange}
        disablePastNavigation
        onDatesSetRef={onDatesSetRef}
      />
      <Box
        ref={hostRef}
        flex="1"
        minH={0}
        position="relative"
        overflow="visible"
        className="im-calendar-fc-scroll-host im-self-schedule"
      >
        <AllDaysCheckbox
          hostElement={hostRef.current}
          isChecked={allChecked}
          isIndeterminate={allIndeterminate}
          isDisabled={allDisabled || allAvailableCount === 0}
          disabledReason={allReason}
          onToggle={toggleAll}
        />
        <FullCalendar
          key={timezone}
          ref={calendarRef}
          plugins={[interactionPlugin, timeGridPlugin, momentTimezonePlugin]}
          nowIndicator
          initialView="timeGridWeek"
          initialDate={initialDate}
          dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
          dayHeaderContent={renderDayHeader}
          headerToolbar={false}
          selectable={hasInterviewer}
          selectMirror
          dayMaxEvents
          weekends
          height="100%"
          allDaySlot={false}
          slotDuration="00:15:00"
          snapDuration="00:15:00"
          slotLabelInterval={{ hours: 1 }}
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          expandRows
          timeZone={timezone}
          firstDay={1}
          stickyHeaderDates
          slotEventOverlap
          scrollTime={getScrollTimeForNow(timezone)}
          eventOrderStrict
          eventOrder="-extendedProps.priority,start_time"
          events={events}
          datesSet={(arg) => {
            onDatesSetRef.current?.();
            if (onActiveRangeChange && arg?.start && arg?.end) {
              onActiveRangeChange({
                start: moment.tz(arg.start, timezone).toISOString(),
                end: moment.tz(arg.end, timezone).toISOString(),
              });
            }
          }}
          select={handleSelect}
          dateClick={handleDateClick}
          eventDrop={handleEventChange}
          eventResize={handleEventChange}
          eventContent={renderEventContent}
          selectAllow={selectAllow}
          eventAllow={eventAllow}
          slotLabelContent={({ text }) => (
            <KText variant="small_text" w="47px" alignItems="center">
              {text}
            </KText>
          )}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            omitZeroMinute: true,
            meridiem: true,
            separator: ' - ',
          }}
        />
      </Box>
    </Flex>
  );
};
